import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

/**
 * ProtectedRoute - Protege rutas que requieren autenticación.
 *
 * Props:
 *   children     - Componente a renderizar si pasa todos los checks.
 *   roles        - Array de roles permitidos, ej: ['admin'] o ['buyer', 'seller', 'both'].
 *                  Si no se pasa, cualquier rol autenticado tiene acceso.
 *   allowPending - Si es true, usuarios con status pending/suspended/rejected
 *                  pueden acceder (útil para la propia página /pending-approval).
 */
const ProtectedRoute = ({ children, roles, allowPending = false }) => {
    const {
        user,
        profile,
        loading,
        isAdmin,
        needsEmailVerification,
        isPendingApproval,
        isAccountSuspended,
        isAccountRejected,
    } = useAuth();

    const location = useLocation();

    // Subscription check — only for blocked (pending) users
    const [subChecked, setSubChecked] = useState(false);
    const [hasSub, setHasSub] = useState(false);

    const isBlocked = !isAdmin && (isPendingApproval || isAccountSuspended || isAccountRejected);

    useEffect(() => {
        if (!isBlocked || allowPending || !user?.id) {
            setSubChecked(true);
            return;
        }

        const checkSub = async () => {
            try {
                // Primero obtener company_profile_id
                const { data: cp } = await supabase
                    .from('company_profiles')
                    .select('id')
                    .eq('user_id', user.id)
                    .maybeSingle();

                if (!cp?.id) { setHasSub(false); setSubChecked(true); return; }

                // Luego buscar suscripción por company_profile_id
                const { data: sub } = await supabase
                    .from('subscriptions')
                    .select('id')
                    .eq('company_profile_id', cp.id)
                    .in('status', ['active', 'pending'])
                    .maybeSingle();

                setHasSub(!!sub);
            } catch {
                setHasSub(false);
            } finally {
                setSubChecked(true);
            }
        };
        checkSub();
    }, [isBlocked, allowPending, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // 1. Verificando sesión — mostrar spinner
    if (loading || (isBlocked && !allowPending && !subChecked)) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-muted-foreground">Verificando sesión...</p>
                </div>
            </div>
        );
    }

    // 2. No autenticado → /login (guardando la ruta intentada para redirigir después)
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // 3. Email sin verificar → /email-verification
    if (needsEmailVerification) {
        return <Navigate to="/email-verification" replace />;
    }

    // 4. Cuenta bloqueada (pending/suspended/rejected)
    //    Si no tiene suscripción → /plans; si tiene → /pending-approval
    //    Los admins quedan exentos de este check (su status suele ser null).
    if (isBlocked && !allowPending) {
        return hasSub
            ? <Navigate to="/pending-approval" replace />
            : <Navigate to="/plans" replace />;
    }

    // 5. Verificación de rol
    if (roles && roles.length > 0) {
        const userRole = profile?.role;
        const hasRequiredRole = roles.some((requiredRole) => {
            if (requiredRole === 'buyer') return userRole === 'buyer' || userRole === 'both';
            if (requiredRole === 'seller') return userRole === 'seller' || userRole === 'both';
            return userRole === requiredRole;
        });

        if (!hasRequiredRole) {
            // Redirigir a dashboard con estado de error para mostrar mensaje
            return <Navigate to="/dashboard" state={{ unauthorized: true }} replace />;
        }
    }

    // 6. Todo OK → renderizar la ruta
    return children;
};

export default ProtectedRoute;
